import apiClient, { ApiResponse } from '../api/client';
import { User } from '../types/models';

export interface LoginCredentials {
  email: string;
  password: string;
  remember?: boolean;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
  county_id: number;
}

export interface AuthResponse {
  user: User;
  token: string;
}

class AuthService {
  // Login
  async login(credentials: LoginCredentials): Promise<ApiResponse<AuthResponse>> {
    try {
      const response = await apiClient.post<AuthResponse>('/admin/login', credentials);

      if (response.status === 'success' && response.data) {
        apiClient.setAuthToken(response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }

      return response;
    } catch (error: any) {
      // Re-throw with better error message
      throw error;
    }
  }

  // Register
  async register(data: RegisterData): Promise<ApiResponse<AuthResponse>> {
    const response = await apiClient.post<AuthResponse>('/admin/register', data);

    if (response.status === 'success' && response.data) {
      apiClient.setAuthToken(response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }

    return response;
  }

  // Logout
  async logout(): Promise<ApiResponse> {
    const userType = this.getUserType();
    const endpoint = userType === 'superadmin' ? '/admin/superadmin/logout' : '/admin/county/logout';
    const response = await apiClient.post(endpoint);
    apiClient.clearAuth();
    return response;
  }

  // Get current user
  async getCurrentUser(): Promise<ApiResponse<User>> {
    const userType = this.getUserType();
    const endpoint = userType === 'superadmin' ? '/admin/superadmin/user' : '/admin/county/user';
    return apiClient.get<User>(endpoint);
  }

  // Update profile
  async updateProfile(data: Partial<User>): Promise<ApiResponse<User>> {
    const userType = this.getUserType();
    const endpoint = userType === 'superadmin' ? '/admin/superadmin/profile' : '/admin/county/profile';
    return apiClient.put<User>(endpoint, data);
  }

  // Change password
  async changePassword(data: { current_password: string; new_password: string; new_password_confirmation: string }): Promise<ApiResponse> {
    const userType = this.getUserType();
    const endpoint = userType === 'superadmin' ? '/admin/superadmin/change-password' : '/admin/county/change-password';
    return apiClient.post(endpoint, data);
  }

  // Get user type from stored user
  getUserType(): 'superadmin' | 'county' {
    const user = this.getStoredUser();
    return user?.user_type === 'superadmin' ? 'superadmin' : 'county';
  }

  // Get stored user
  getStoredUser(): User | null {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  // Check if authenticated
  isAuthenticated(): boolean {
    return !!localStorage.getItem('auth_token');
  }
}

export const authService = new AuthService();
export default authService;
